import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website_url: '', // Honeypot field
  });

  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          website_url: '',
        });
      } else {
        throw new Error('Der opstod en fejl ved afsendelse af formularen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setStatus('error');
      setErrorMessage(err.message || 'Noget gik galt. Prøv igen senere eller ring direkte.');
    }
  };

  return (
    <section id="kontakt" className="py-24 bg-white relative overflow-hidden">
      {/* Background glow shapes */}
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#487aad]/3 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-12 left-12 w-[300px] h-[300px] bg-blue-500/3 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Left Column: Contact Info */}
          <div className="lg:col-span-5 flex flex-col justify-between">
            <div>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-black uppercase">
                Lad os samarbejde om dit næste projekt
              </h2>
              <p className="mt-4 text-slate-600 text-base leading-relaxed">
                Har du spørgsmål til vores ydelser, eller ønsker du et uforpligtende tilbud? Udfyld formularen, eller kontakt os direkte via telefon eller e-mail. Vi vender hurtigt tilbage!
              </p>

              {/* Contact Details List */}
              <div className="mt-10 space-y-4">
                <a 
                  href="tel:+4593872382" 
                  className="flex items-center gap-4 py-4 border-b border-slate-200 hover:border-black transition-colors group"
                >
                  <div className="text-black shrink-0">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 uppercase tracking-wider block">Ring til os</span>
                    <span className="text-lg font-bold text-slate-900 block mt-0.5">93 87 23 82</span>
                  </div>
                </a>

                <a 
                  href="mailto:balsbymontage@gmail.com" 
                  className="flex items-center gap-4 py-4 border-b border-slate-200 hover:border-black transition-colors group"
                >
                  <div className="text-black shrink-0">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 uppercase tracking-wider block">Send en mail</span>
                    <span className="text-lg font-bold text-slate-900 block mt-0.5 break-all">balsbymontage@gmail.com</span>
                  </div>
                </a>

                <div className="flex items-center gap-4 py-4 border-b border-slate-200">
                  <div className="text-black shrink-0">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 uppercase tracking-wider block">Besøg os</span>
                    <span className="text-lg font-bold text-slate-900 block mt-0.5">Fladhøjvej 1, 6818 Årre</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Card Info */}
            <div className="mt-12 pt-8 border-t border-slate-100 text-xs text-slate-400 space-y-1">
              <p className="font-semibold text-slate-500">Balsby Montage ApS</p>
              <p>CVR-nummer: 43732358</p>
              <p>Adresse: Fladhøjvej 1, 6818 Årre</p>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-100 p-6 sm:p-10 shadow-xl relative z-10">
              <h3 className="text-xl font-bold text-black uppercase mb-6">Send os en besked</h3>
              
              {status === 'success' ? (
                <div className="bg-emerald-500/5 border border-emerald-500/20 p-6 text-center animate-in fade-in duration-300">
                  <div className="inline-flex items-center justify-center bg-emerald-500 text-white p-3 mb-4">
                    <CheckCircle className="h-6 w-6" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900">Beskeden er sendt!</h4>
                  <p className="text-slate-600 text-sm mt-2">
                    Tak for din henvendelse. Vi behandler din besked hurtigst muligt og vender tilbage til dig snarest.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-6 text-sm font-bold text-[#487aad] hover:text-[#3b6591] underline cursor-pointer"
                  >
                    Send endnu en besked
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field (absolutely hidden from humans) */}
                  <div className="sr-only absolute -top-[9999px] -left-[9999px]">
                    <label htmlFor="website_url">Do not fill this field if you are human</label>
                    <input
                      id="website_url"
                      type="text"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                        Navn <span className="text-[#487aad]">*</span>
                      </label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Dit fulde navn"
                        className="w-full bg-white border border-slate-200 focus:border-[#487aad] focus:ring-1 focus:ring-[#487aad] px-4 py-3 text-slate-900 placeholder-slate-400 text-sm transition-all outline-none"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label htmlFor="phone" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                        Telefonnummer <span className="text-[#487aad]">*</span>
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="F.eks. 12 34 56 78"
                        className="w-full bg-white border border-slate-200 focus:border-[#487aad] focus:ring-1 focus:ring-[#487aad] px-4 py-3 text-slate-900 placeholder-slate-400 text-sm transition-all outline-none"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                      E-mailadresse <span className="text-[#487aad]">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="din@mail.dk"
                      className="w-full bg-white border border-slate-200 focus:border-[#487aad] focus:ring-1 focus:ring-[#487aad] px-4 py-3 text-slate-900 placeholder-slate-400 text-sm transition-all outline-none"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                      Besked <span className="text-[#487aad]">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Beskriv dit projekt eller hvad du har brug for hjælp til..."
                      className="w-full bg-white border border-slate-200 focus:border-[#487aad] focus:ring-1 focus:ring-[#487aad] px-4 py-3 text-slate-900 placeholder-slate-400 text-sm transition-all outline-none resize-none"
                    />
                  </div>

                  {/* Error Message */}
                  {status === 'error' && (
                    <div className="bg-red-500/5 border border-red-500/20 p-4 flex items-start gap-3 text-red-600 text-sm">
                      <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                      <div>
                        <h5 className="font-bold">Der opstod en fejl</h5>
                        <p className="mt-0.5 text-xs text-red-500/90">{errorMessage}</p>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full flex items-center justify-center gap-2 bg-[#487aad] hover:bg-[#3b6591] disabled:bg-slate-200 text-white disabled:text-slate-400 font-bold py-4 transition-all duration-200 shadow-md shadow-[#487aad]/10 hover:shadow-[#487aad]/20 cursor-pointer"
                  >
                    {status === 'loading' ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Sender besked...
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        Send besked
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
